import { imoscheme, ircsscheme, regnoscheme } from '#vid/constants';
import { useVesselOracle } from '#vessel/oracle';
import { SlotFormProps } from '#vessel/overview';
import { useVID } from '#vid/model';
import { VidBox } from '#vid/vidbox';
import { useT } from 'apprise-frontend-core/intl/language';
import { useOracle } from 'apprise-frontend-iam/authz/oracle';
import { useTenantStore } from 'apprise-frontend-iam/tenant/store';
import { TenantBox } from 'apprise-frontend-iam/tenant/tenantbox';
import { useTagModel } from 'apprise-frontend-tags/tag/model';
import { useTagStore } from 'apprise-frontend-tags/tag/store';
import { TagBoxes } from 'apprise-frontend-tags/tag/tagboxes';
import { TagmapBox } from 'apprise-frontend-tags/tag/tagmapbox';
import { useTagValidation } from 'apprise-frontend-tags/validation';
import { FieldInfo } from 'apprise-ui/field/model';
import { Fields, error, useValidation } from 'apprise-ui/field/validation';
import { Form } from 'apprise-ui/form/form';
import { NumberBox } from 'apprise-ui/numberbox/numberbox';
import { useRecordFields } from 'apprise-ui/recordbox/recordbox';
import { TextBox } from 'apprise-ui/textbox/textbox';
import { Fragment } from 'react';
import { beneficialOwnerTag, carrierKindTag, contactTypeCategory, detailsType, fishingKindTag, grtTag, loaTag, minVolume, operatingCompanyTag, operatorTag, ownerTag, portRegistryCategory, specificationCategory, supplyVesselTypeTag, tonnageTag, totalVolumeTag, totalVolumeTonnesTag, vesselGearCategory, vesselKindCategory, vesselRangeCategory, vesselTypeCategory } from './constants';
import { ContactBox } from './contactbox';
import { Contact, Details } from './model';
import { maxLoa, maxTonnage, minLoa, minTonnage, useMaxVolumes } from './validator';
import { TagReference } from 'apprise-core/tag';
import { TagBox } from 'apprise-frontend-tags/tag/tagbox';


export const DetailsForm = (props: SlotFormProps) => {

    return <Form fieldDefaults={{ help: null, trackChange: true }}>

        <DetailsFormFields {...props} />

    </Form>
}

export const DetailsFormFields = (props: SlotFormProps) => {

    const { current, edited, initial, set } = props

    const vids = useVID()
    const tags = { ...useTagStore(), ...useTagModel() }

    const validation = useValidation()

    const fields = useDetailFields(props)

    const report = validation.reportOf(fields)

    const details = edited.details
    const pastDetails = initial?.details ?? {} as Details

    const logged = useOracle()
    const oracle = useVesselOracle()

    const tenants = useTenantStore()

    const schemes = vids.allSchemes()

    const canChangeTenant = logged.managesMultipleTenants() && (oracle.canClaim(current) || current.lifecycle.state === 'uploaded')


    const allowedTenants = tenants.allSorted().filter(t => oracle.canAddFor(t.id)).sort()

    return <Fragment>

        {canChangeTenant &&

            <TenantBox tenants={allowedTenants} noClear info={report.flagstate} noReadonly={oracle.canClaim(current)} onChange={set.with((m, v) => {
                m.tenant = v
                m.details.flagstate = v
            })}>
                {details.flagstate}
            </TenantBox>

        }

        <TextBox info={fields.name} pastValue={pastDetails.name} onChange={set.with((m, v) => m.details.name = v)}>
            {details?.name}
        </TextBox>

        {schemes.map(scheme => {

            var match = details.identifiers.find(id => vids.schemeOf(id) === scheme)
            var previousmatch = current.details.identifiers.find(id => vids.schemeOf(id) === scheme)
            var matchIndex = match ? details.identifiers.indexOf(match) : details.identifiers.length
            var readonly = !!previousmatch && scheme.id === imoscheme && !vids.isSpecialValue(previousmatch) && logged.isTenantUser()

            return <VidBox
                key={scheme.id}
                schemes={scheme.id}
                info={report[scheme.id as any]}
                readonly={readonly}
                onChange={set.with((m, v) => m.details.identifiers[matchIndex] = v)}>
                {match}
            </VidBox>
        })}

        <TagBox status={report[vesselKindCategory]?.status} category={vesselKindCategory} pastValue={pastDetails.vesselKind} onChange={set.with((m, v) => {

            m.details.vesselKind = v

            // clear any TRX authz on transitioning to FV from CV.
            if (v === fishingKindTag)
                delete m.trxauthz

            // m.details.vesselType = undefined!
        })}>
            {details.vesselKind}
        </TagBox>

        <TagBox /* enabled={!!details.vesselKind} */ status={report[vesselTypeCategory]?.status} msg={report[vesselTypeCategory]?.msg}
            category={vesselTypeCategory}
            tags={
                tags.allTagsOfCategory(vesselTypeCategory)
                //.filter(t => (t.properties.custom?.[vessselKindProp] ?? fishingKindTag) === details.vesselKind)
            }
            pastValue={pastDetails.vesselType} onChange={set.with((m, v) => m.details.vesselType = v)}>
            {details.vesselType}
        </TagBox>

        <TagBoxes categories={[vesselRangeCategory, vesselGearCategory]} report={report}
            pastValue={[pastDetails.range, ...(pastDetails.gears ?? [])].filter(t => !!t)}
            onChange={set.with((m, v) => {

                const operatingRangeTags = v.filter(t => tags.lookupTag(t)?.category === vesselRangeCategory)
                const gears = v.filter(t => tags.lookupTag(t)?.category === vesselGearCategory)

                const range = operatingRangeTags.length > 0 ? operatingRangeTags[0] : ''


                m.details = { ...m.details, range, gears }

            })}>
            {[details.range, ...(details.gears ?? [])].filter(t => !!t)}
        </TagBoxes>

        <TextBox info={fields.port} pastValue={pastDetails.port?.name} onChange={set.with((m, v) => m.details.port = { ...m.details.port, name: v })}>
            {details.port.name}
        </TextBox>

        <TextBox info={fields.portcode} pastValue={pastDetails.port?.code} onChange={set.with((m, v) => m.details.port = { ...m.details.port, code: v })}>
            {details.port.code}
        </TextBox>

        <TextBox info={fields.portflag} pastValue={pastDetails.port?.flag} onChange={set.with((m, v) => m.details.port = { ...m.details.port, flag: v })}>
            {details.port.flag}
        </TextBox>

        <TextBox info={fields.priorHistoryName} pastValue={pastDetails.priorHistory?.name} onChange={set.with((m, v) => m.details.priorHistory = { ...m.details.priorHistory, name: v })}>
            {details.priorHistory?.name}
        </TextBox>

        <TextBox info={fields.priorHistoryFlags} pastValue={pastDetails.priorHistory?.flags} onChange={set.with((m, v) => m.details.priorHistory = { ...m.details.priorHistory, flags: v })}>
            {details.priorHistory?.flags}
        </TextBox>

        <TextBox info={fields.priorHistoryDeletions} pastValue={pastDetails.priorHistory?.deletions} onChange={set.with((m, v) => m.details.priorHistory = { ...m.details.priorHistory, deletions: v })}>
            {details.priorHistory?.deletions}
        </TextBox>

        <TagmapBox category={specificationCategory} include={t => t.id !== totalVolumeTonnesTag && t.id !== grtTag} renderValue={() => <NumberBox width='100%' />} validate={fields.specificationValidators} onChange={set.with((m, v) => m.details.specifications = v)}>
            {details.specifications}
        </TagmapBox>

        {tags.allTagsOfCategory(contactTypeCategory)
            .sort(tags.codeComparator)
            .map(type => (details.contacts?.find(c => c.type === type.id) ?? { type: type.id }) as Contact)
            .map((contact, i) =>

                <ContactBox validate={fields.contactValidators} key={i} onChange={set.with((m, v) => {

                    const index = m.details.contacts?.findIndex(c => c.type === v.type)

                    if (index < 0)
                        m.details.contacts = [...m.details.contacts ?? [], v]
                    else {

                        // if this was a new 
                        if (Object.keys(v).length === 0 && !initial.details?.contacts.find(c => c.type === v.type))

                            m.details.contacts = m.details.contacts.filter((_, i) => i !== index)

                        else

                            m.details.contacts[index] = v
                    }

                })}>
                    {contact}
                </ContactBox>


            )}

    </Fragment>
}


export const useDetailFields = (props: SlotFormProps) => {

    const t = useT()
    const vids = useVID()

    const tags = { ...useTagStore(), ...useTagModel(), ...useTagValidation() }

    const { check, is } = useValidation()

    const { edited, initial, current, isNew } = props

    const details = edited?.[detailsType] ?? {}

    const isCarrierVessel = details.vesselKind === carrierKindTag

    const { maxVolumeFV, maxVolumeCV } = useMaxVolumes()

    // the validator warns over the same max, by kind.
    const maxVolume = isCarrierVessel ? maxVolumeCV : maxVolumeFV

    const specificationValidators = {

        [loaTag]: (s: string) => ({

            ...check(is.empty).and(is.isNaN).and(is.number.smallerOrEqualThan(minLoa)).on(s),
            ...check(is.number.greaterThan(maxLoa)).provided(!!s).given(s).asWarning()

            , location: loaTag
        }),

        // GT
        [tonnageTag]: (s: string, r: Record<TagReference, string>) => {

            const checks = s ? {
                ...check(is.isNaN).and(is.number.smallerOrEqualThan(minTonnage)).on(s),
                ...check(is.number.greaterThan(maxTonnage)).given(s).asWarning()
            }

                :

                // required only on new records mor on records that do not have a GRT
                isCarrierVessel ?
                    check(is.empty).and(is.isNaN).on(s)
                    :
                    check(is.empty).provided(!current || !current.details.specifications[grtTag]).on(s)

            return {


                ...checks, location: tonnageTag
            }
        },

        [totalVolumeTag]: (s: string) => ({
            ...check(is.isNaN).and(is.empty).on(s),
            ...check(is.number.smallerOrEqualThan(minVolume)).provided(!!s).on(s),
            ...check(is.number.greaterThan(maxVolume)).provided(!!s).given(s).asWarning()


            , location: totalVolumeTag
        })


        ,

        // legacy and not fielded
        // [grtTag]: (s: string, r: Record<TagReference, string>) => {


        //     return {
        //          ...check(is.number.smallerOrEqualThan(minGrt)).provided(!!s).on(s),
        //          ...check(is.isNaN).and(is.number.greaterThan(maxGrt)).provided(!!s).given(s).asWarning(),


        //         location: grtTag }
        // }
    }

    const specificationFields = useRecordFields(specificationValidators)



    let categoriesToValidate = [vesselKindCategory, vesselTypeCategory, vesselRangeCategory]

    if (details.vesselKind === fishingKindTag)
        categoriesToValidate.push(vesselGearCategory)

    let reflaggingIssues = {}

    // a vessel being created is not being re-flagged: its first flag is not a change of flag.
    // 'current' is the blank minted record here, not undefined, so it cannot carry the guard itself.
    // gating on isNew rather than on a missing previous flag leaves deflagged vessels checked.
    const reflagged = (!isNew && current && details && details.flagstate && current[detailsType].flagstate !== details.flagstate)

    // only a check that actually fired carries a message. msg doubles as the field's hint in this
    // DSL, so setting it unconditionally used to replace both the hint and any 'can't be empty'.
    const onReflag = (outcome: FieldInfo | undefined | false, msg: string) => outcome ? { ...outcome, msg } : {}

    const currentRegNo = vids.valueOf(current[detailsType].identifiers?.find(i => vids.schemeOf(i)?.id === regnoscheme))
    const currentIrcs = vids.valueOf(current[detailsType].identifiers?.find(i => vids.schemeOf(i)?.id === ircsscheme))
    const currentPortName = current[detailsType].port?.name
    const currentPortCode = current[detailsType].port?.code

    const getCurrentContactFor = (contact: Contact) => current[detailsType].contacts.find(c => c.type === contact.type) ?? {} as Contact

    if (reflagged) {

        reflaggingIssues = {

            [regnoscheme]: onReflag(

                check(is.string.equalIgnoreCase(currentRegNo)).on(vids.valueOf(details.identifiers?.find(i => vids.schemeOf(i)?.id === regnoscheme)))

                , t('valid.invalid_same_regno_on_reflag'))

            ,

            [ircsscheme]: onReflag(

                check(is.string.equalIgnoreCase(currentIrcs)).on(vids.valueOf(details.identifiers?.find(i => vids.schemeOf(i)?.id === ircsscheme)))

                , t('valid.invalid_same_ircs_on_reflag'))

            ,

            port: onReflag(

                check(is.string.equalIgnoreCase(currentPortName)).on(details.port?.name)

                , t('valid.invalid_same_port_on_reflag'))

            ,

            portcode: onReflag(

                check(is.string.equalIgnoreCase(currentPortCode)).on(details.port?.code)

                , t('valid.invalid_same_port_on_reflag'))

            ,

            contactFields: (contact: Contact) => {

                const msg = contact.type === ownerTag ? t('valid.warn_same_owner_on_reflag')
                    : contact.type === operatorTag ? t('valid.warn_same_operator_on_reflag')
                    : contact.type === beneficialOwnerTag ? t('valid.warn_same_beneficial_on_reflag')
                    : contact.type === operatingCompanyTag ? t('valid.warn_same_operating_on_reflag')
                    : t('valid.invalid_contact')

                return {
                    name: (s: string) => onReflag(check(is.string.equalIgnoreCase(getCurrentContactFor(contact).name)).given(s).asWarning(), msg),
                    address: (s: string) => onReflag(check(is.string.equalIgnoreCase(getCurrentContactFor(contact).address)).given(s).asWarning(), msg),
                    regno: (s: string) => onReflag(check(is.string.equalIgnoreCase(getCurrentContactFor(contact)['regno'])).provided(contact.type === operatingCompanyTag).given(s).asWarning(), msg)
                }
            }


        }

    }

    const contactValidators = {

        name: (s: string, contact: Contact) => ({ ...check(is.empty).on(s), ...reflaggingIssues['contactFields']?.(contact).name?.(s) }),
        address: (s: string, contact: Contact) => ({ ...check(is.empty).on(s), ...reflaggingIssues['contactFields']?.(contact).address?.(s) }),
        regno: (s: string, contact: Contact) => ({ ...check(is.empty).provided(contact.type === operatingCompanyTag).on(s), ...reflaggingIssues['contactFields']?.(contact).regno?.(s) })
    }

    const contactFields = useRecordFields(contactValidators)


    const self = {

        flagstate: {

            label: t('details.flag'),
            msg: t('details.flag_msg'),
            ...check(is.empty).on(details.flagstate),
            location: 'flagstate'
        },

        name: {

            label: t('details.name'),
            msg: t('details.name_msg'),
            help: t('details.name_help'),
            ...check(is.empty).on(details.name),
            location: 'name'
        },

        [imoscheme]: {

            ...check(is.empty)
                .and(imo => !vids.isSpecialValue(imo) && !vids.isValidIMO(imo) && error(t('valid.invalid_imo')))
                .on(vids.valueOf(details.identifiers?.find(i => vids.schemeOf(i)?.id === imoscheme))),
            location: imoscheme

        },

        [regnoscheme]: {

            ...check(is.empty).on(vids.valueOf(details.identifiers?.find(i => vids.schemeOf(i)?.id === regnoscheme))),
            location: regnoscheme,
            ...(reflaggingIssues[regnoscheme] ?? {})

        },

        [ircsscheme]: {

            ...check(is.empty).on(vids.valueOf(details.identifiers?.find(i => vids.schemeOf(i)?.id === ircsscheme))),
            location: ircsscheme,
            ...(reflaggingIssues[ircsscheme] ?? {})
        },

        port: {

            label: t('details.port'),
            msg: t('details.port_msg'),
            help: t('details.port_help'),
            ...!isCarrierVessel ? { ...check(is.empty).on(details.port?.name) } : {},
            location: portRegistryCategory,
            ...(reflaggingIssues['port'] ?? {})
        },

        portcode: {

            label: t('details.portcode'),
            msg: t('details.portcode_msg'),
            help: t('details.portcode_help'),
            ...(reflaggingIssues['portcode'] ?? {})
        },

        portflag: {

            label: t('details.portflag'),
            msg: t('details.portflag_msg'),
            help: t('details.portflag_help')
        },

        priorHistoryName: {
            label: t('details.prev_name'),
            msg: t('details.prev_name_msg')
        }

        ,

        priorHistoryFlags: {
            label: t('details.prev_flags'),
            msg: t('details.prev_flags_msg')
        }

        ,

        priorHistoryDeletions: {
            label: t('details.prev_deletions'),
            msg: t('details.prev_deletions_msg')
        }

        ,

        ...specificationFields(details.specifications)

        ,

        ...tags.validateTags([details.vesselKind, details.vesselType, details.range, ...details.gears])
            .include(...categoriesToValidate)

        ,

        specificationValidators

        ,

        ...tags.allTagsOfCategory(contactTypeCategory).sort(tags.codeComparator)
            .map(typeTag => (details.contacts?.find(c => c.type === typeTag.id) ?? { type: typeTag.id }) as Contact)
            .map(contact => [contact, contactFields(contact)] as const).reduce((acc, [contact, field]) => ({

                ...acc,
                ...Object.keys(field).reduce((a, key) => ({ ...a, [`${contact.type}-${key}`]: { ...field[key], location: `${contact.type}-${key}` } }), {})

            }), {} as Fields)

        ,

        contactValidators

    }

    // custom validation check for "new" supply vessels
    self[vesselTypeCategory] = {

        ...self[vesselTypeCategory],

        ...check(type => type !== initial?.details.vesselType && type === supplyVesselTypeTag && { status: 'warning', msg: t('valid.invalid_supply_type'), location: vesselTypeCategory },).on(details.vesselType)

    }


    return self

}
